import { ActionButton } from 'elements/minute/sidebar/sidebarComponents/atomic/ActionButton';
import { SelectValue } from 'elements/minute/sidebar/sidebarComponents/atomic/SelectValue';
import { TextValue } from 'elements/minute/sidebar/sidebarComponents/atomic/TextValue';
import { ColorPalette } from 'elements/minute/sidebar/sidebarComponents/complex/ColorPalette';
import { SidebarContent } from 'elements/minute/sidebar/sidebarSection/SidebarContent';
import { SidebarSubsection } from 'elements/minute/sidebar/sidebarSection/SidebarSubsection';
import { recalculateCoordinatesForNewImage } from 'hooks/coordinates/useCoordinatesComputations';
import { useUploadImage } from 'libs/hooks/io/useUploadImage';
import { BACKGROUND_IMAGE_DEFAULT_COORDINATES } from 'libs/utils/map/mapImageBackground';
import { useContext, useEffect, useState } from 'react';
import { CtxEdit } from 'session/ctxEdit';
import { BLANK_STYLE, useStyles } from 'static/config/stylesConfig';
import { Localization } from 'static/localization/localization';
import styled from 'styled-components/macro';

type MapStyle = 'Basic' | 'Colored' | 'Image map' | 'Custom';

interface Props {
    className?: string;
}

const CUSTOM_DEFAULT_VALUE = 'custom';

export const MapSettingsStyleSubsection = (props: Props): JSX.Element => {
    const { BASIC_STYLES, COLOR_STYLES } = useStyles();
    const OPTION_COLORED = {
        label: Localization.COLORED,
        value: COLOR_STYLES[0].url
    };

    const OPTION_IMAGE = {
        label: Localization.IMAGE_MAP,
        value: BLANK_STYLE
    };
    const OPTION_CUSTOM = {
        label: Localization.CUSTOM,
        value: CUSTOM_DEFAULT_VALUE
    };

    const ctxEdit = useContext(CtxEdit);
    const map = ctxEdit.map;

    const [mapStyle, setMapStyle] = useState<MapStyle>(getMapStyle(ctxEdit.map.defaultStyles[0]));
    const { uploaded, setFiles, inProgress } = useUploadImage([4096], ctxEdit.map?.worldMapId);

    useEffect(() => {
        if (!uploaded?.data?.imageUrls || !ctxEdit.mapRef?.current) {
            return;
        }

        const imageUrl = uploaded?.data?.imageUrls[4096];

        ctxEdit.mapRef.current.loadImage(imageUrl, (error, image) => {
            const newCoordinates = recalculateCoordinatesForNewImage(
                ctxEdit.mapRef.current,
                BACKGROUND_IMAGE_DEFAULT_COORDINATES,
                image
            );
            ctxEdit.updateMap({
                imageBackgroundUrl: imageUrl,
                imageBackgroundCoordinates: newCoordinates.map((e) => ({
                    ...e,
                    __typename: 'Coordinates'
                }))
            });

            ctxEdit.mapRef.current?.fitBounds(
                [
                    [newCoordinates[0].lng, newCoordinates[0].lat],
                    [newCoordinates[2].lng, newCoordinates[2].lat]
                ],
                {
                    padding: 100,
                    animate: false
                }
            );
        });
    }, [uploaded]);

    const options = BASIC_STYLES.map((e) => ({
        label: e.name,
        value: e.url
    })).concat(OPTION_COLORED, OPTION_IMAGE, OPTION_CUSTOM);

    function getMapStyle(value: string): MapStyle {
        if (BASIC_STYLES.find((e) => e.url === value)) {
            return 'Basic';
        } else if (COLOR_STYLES.find((e) => e.url === value)) {
            return 'Colored';
        } else if (value == BLANK_STYLE) {
            return 'Image map';
        } else {
            return 'Custom';
        }
    }

    function getMapStyleOption(style: string) {
        if (style === BLANK_STYLE) {
            return OPTION_IMAGE;
        }
        if (COLOR_STYLES.find((e) => e.url === style)) {
            return OPTION_COLORED;
        }

        const option = BASIC_STYLES.find((e) => e.url === style);
        if (option) {
            return {
                label: option.name,
                value: option.url
            };
        }

        return OPTION_CUSTOM;
    }

    return (
        <S_MapSettingsStyleSubsection className={props.className}>
            <SidebarSubsection title={Localization.MAP_STYLE} icon={{ iconName: 'earth-americas' }}>
                <S_SidebarContent>
                    <SelectValue
                        options={options}
                        selectedOption={getMapStyleOption(map.defaultStyles[0])}
                        onChange={(selected) => {
                            const style = getMapStyle(selected.value);
                            setMapStyle(style);
                            if (style === 'Basic') {
                                ctxEdit.updateMap({ defaultStyles: [selected.value] });
                            }
                            if (style === 'Colored') {
                                ctxEdit.updateMap({ defaultStyles: [COLOR_STYLES[0].url] });
                            }
                            if (style === 'Image map') {
                                ctxEdit.updateMap({ defaultStyles: [BLANK_STYLE] });
                            }
                            if (style === 'Custom') {
                                ctxEdit.updateMap({ defaultStyles: [CUSTOM_DEFAULT_VALUE] });
                            }
                        }}
                    />
                    {mapStyle === 'Colored' && (
                        <S_ColorPalette
                            value={COLOR_STYLES.find((e) => e.url === map.defaultStyles[0])?.color}
                            colors={COLOR_STYLES.map((c) => {
                                return c.color;
                            })}
                            onChange={(color) => {
                                const selectedColor = COLOR_STYLES.find((c) => c.color === color);
                                if (selectedColor) {
                                    ctxEdit.updateMap({
                                        defaultStyles: [selectedColor.url]
                                    });
                                }
                            }}
                        />
                    )}
                    {mapStyle === 'Image map' && (
                        <label>
                            <S_InputFile
                                type="file"
                                accept="image/x-png,image/png,image/gif,image/jpeg,image/jpg,image/heic,image/heif,image/svg+xml,.heic;capture=camera"
                                onChange={(e) => {
                                    setFiles(Array.from(e.target['files']));
                                }}
                            />
                            <S_ActionButton
                                text={
                                    inProgress
                                        ? Localization.UPLOADING + '...'
                                        : Localization.UPLOAD_ACTION
                                }
                                icon={{ iconName: 'upload' }}
                                disabled={Boolean(inProgress)}
                            />
                        </label>
                    )}
                    {mapStyle === 'Custom' && (
                        <S_TextValue
                            value={
                                map.defaultStyles[0] !== CUSTOM_DEFAULT_VALUE
                                    ? map.defaultStyles[0]
                                    : ''
                            }
                            textAlign="left"
                            onChange={(value) => {
                                if (value) {
                                    ctxEdit.updateMap({ defaultStyles: [value] });
                                }
                            }}
                        />
                    )}
                </S_SidebarContent>
            </SidebarSubsection>
        </S_MapSettingsStyleSubsection>
    );
};

const S_MapSettingsStyleSubsection = styled.div``;

const S_ColorPalette = styled(ColorPalette)`
    margin: 20px 0 0 0;
`;

const S_ActionButton = styled(ActionButton)`
    margin: 20px 0 0 0;
`;

const S_TextValue = styled(TextValue)`
    margin-top: 10px;
`;

const S_SidebarContent = styled(SidebarContent)`
    gap: 10px;
`;

const S_InputFile = styled.input`
    display: none;
`;
